import useAccountRecords from '@/features/openTabs/contexts/OpenTabsData/hooks/useAccountRecords'
import useAccountSelection from '@/features/openTabs/contexts/OpenTabsData/hooks/useAccountSelection'
import useAccountSettlement from '@/features/openTabs/contexts/OpenTabsData/hooks/useAccountSettlement'
import useCatalogDraft from '@/features/openTabs/contexts/OpenTabsData/hooks/useCatalogDraft'
import usePaymentSelection from '@/features/openTabs/contexts/OpenTabsData/hooks/usePaymentSelection'
import {
    OpenAccountCatalogValue,
    OpenAccountSettlementValue,
    OpenTabsValue,
} from '@/features/openTabs/types'
import { useFocusEffect } from '@react-navigation/native'
import { FC, PropsWithChildren, createContext, use, useCallback, useMemo, useRef } from 'react'
import invariant from 'tiny-invariant'

const AccountsContext = createContext<OpenTabsValue | undefined>(undefined)
const CatalogContext = createContext<OpenAccountCatalogValue | undefined>(undefined)
const SettlementContext = createContext<OpenAccountSettlementValue | undefined>(undefined)

const OpenTabsDataProvider: FC<PropsWithChildren> = ({ children }) => {
    const settleAccountInFlightRef = useRef(false)
    const records = useAccountRecords(settleAccountInFlightRef)
    const catalog = useCatalogDraft({
        selectedAccount: records.value.selectedAccount,
        refetchSummaries: records.refetchSummaries,
        refetchDetail: records.refetchDetail,
        accountDataStale:
            records.value.detailLoading ||
            records.value.detailFailed ||
            records.value.detailRefreshFailed,
        settleAccountInFlightRef,
    })
    const payment = usePaymentSelection({
        selectedAccount: records.value.selectedAccount,
        detailLoading: records.value.detailLoading,
        detailFailed: records.value.detailFailed || records.value.detailRefreshFailed,
        settleAccountInFlightRef,
    })
    const settlement = useAccountSettlement({
        records,
        catalog,
        selection: payment.paymentSelection,
        selectionMode: payment.paymentSelectionMode,
        clearSelection: payment.clearPaymentSelection,
        settleAccountInFlightRef,
    })
    const selection = useAccountSelection({
        records,
        clearCatalogCart: catalog.value.clearCatalogCart,
        clearPaymentSelection: payment.clearPaymentSelection,
        settlingAccount: settlement.settlingAccount,
    })
    const paymentState = useMemo(() => ({ ...payment, ...settlement }), [payment, settlement])
    const accounts = useMemo(() => ({ ...records.value, ...selection }), [records.value, selection])

    const {
        refetchSummaries,
        refetchDetail,
        refetchSettings,
        value: { selectedAccountId },
    } = records
    const selectedAccountIdRef = useRef(selectedAccountId)
    selectedAccountIdRef.current = selectedAccountId
    const { refetchCatalog, refetchContainerSettings } = catalog

    useFocusEffect(
        useCallback(() => {
            void refetchSummaries().catch(() => undefined)
            void refetchCatalog().catch(() => undefined)
            void refetchContainerSettings().catch(() => undefined)
            if (selectedAccountIdRef.current) void refetchDetail().catch(() => undefined)
            void refetchSettings().catch(() => undefined)
        }, [
            refetchCatalog,
            refetchContainerSettings,
            refetchDetail,
            refetchSummaries,
            refetchSettings,
        ])
    )

    return (
        <AccountsContext.Provider value={accounts}>
            <CatalogContext.Provider value={catalog.value}>
                <SettlementContext.Provider value={paymentState}>
                    {children}
                </SettlementContext.Provider>
            </CatalogContext.Provider>
        </AccountsContext.Provider>
    )
}

export default OpenTabsDataProvider

export const useOpenTabs = (): OpenTabsValue => {
    const context = use(AccountsContext)
    invariant(context, 'useOpenTabs must be used within an OpenTabsDataProvider')
    return context
}

export const useOpenAccountCatalog = (): OpenAccountCatalogValue => {
    const context = use(CatalogContext)
    invariant(context, 'useOpenAccountCatalog must be used within an OpenTabsDataProvider')
    return context
}

export const useOpenAccountSettlement = (): OpenAccountSettlementValue => {
    const context = use(SettlementContext)
    invariant(context, 'useOpenAccountSettlement must be used within an OpenTabsDataProvider')
    return context
}
